// /admin/launch — Richie's single console for declaring the platform's launch
// phase (Phase 0 / 1 / 2 per PRD §11) and toggling each gateable feature
// between off · founding cohort · all. The page is operator-friendly, not
// engineer-friendly: descriptions are plain-English and "Apply Phase N
// defaults" lets a non-technical operator move the whole platform with one
// click + a diff confirm.
//
// API contract (worker, built in parallel):
//   GET    /api/admin/launch                       → { phase, features, recentChanges }
//   PATCH  /api/admin/launch/phase                 → body { phase }
//   PATCH  /api/admin/launch/features/:key         → body { visibility }
//   POST   /api/admin/launch/apply-recommended     → body { phase } → { diff }
//
// The shared registry at @wwaave/shared/feature-flags is the source of truth
// for labels, descriptions, categories, and per-phase recommended values; we
// never hard-code those here.

import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Progress,
  SegmentedControl,
  Select,
  Skeleton,
  Spoiler,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  FEATURE_CATEGORY_LABELS,
  FEATURE_FLAGS,
  type FeatureCategory,
  type FeatureFlagKey,
  type FeatureVisibility,
  isLaunchPhase,
  LAUNCH_PHASE_LABELS,
  LAUNCH_PHASES,
  type LaunchPhase,
} from '@wwaave/shared/feature-flags';
import { useMemo, useState } from 'react';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { adminGet, adminPatch, adminPost } from './_api';

// ─── API response shapes ──────────────────────────────────────────────────

type LaunchStateResponse = {
  phase: LaunchPhase;
  features: Record<FeatureFlagKey, FeatureVisibility>;
  recentChanges: AuditEntry[];
};

type AuditEntry = {
  id: string;
  action: string;
  payload: Record<string, unknown> | null;
  createdAt: string | number;
  actorDisplayName: string | null;
};

type Cohort = { claimed: number; cap: number; remaining: number };

type ApplyPresetResponse = {
  diff: Array<{ key: string; from: FeatureVisibility; to: FeatureVisibility }>;
};

// ─── Static helpers ───────────────────────────────────────────────────────

const CATEGORY_ORDER: readonly FeatureCategory[] = [
  'trust',
  'commerce',
  'discovery',
  'communications',
] as const;

const VISIBILITY_OPTIONS: Array<{ label: string; value: FeatureVisibility }> = [
  { label: 'Off', value: 'off' },
  { label: 'Founding cohort', value: 'founding_cohort' },
  { label: 'All', value: 'all' },
];

function visibilityLabel(v: FeatureVisibility): string {
  if (v === 'off') return 'off';
  if (v === 'founding_cohort') return 'founding cohort';
  return 'all';
}

function phaseSelectOptions(): Array<{ value: LaunchPhase; label: string }> {
  return LAUNCH_PHASES.map((p) => ({ value: p, label: LAUNCH_PHASE_LABELS[p].label }));
}

/**
 * Tiny relative-time formatter. Local to this route — the codebase has a few
 * similar hand-rolls (see /notifications, /exchanges, inbox helpers); none is
 * exported from a shared module yet, so we duplicate rather than reach across
 * route boundaries.
 */
function formatRelativeTime(input: string | number): string {
  const ms = typeof input === 'number' ? input : Date.parse(input);
  if (!Number.isFinite(ms)) return '';
  const deltaSec = Math.max(0, (Date.now() - ms) / 1000);
  if (deltaSec < 45) return 'just now';
  if (deltaSec < 60 * 60) {
    const m = Math.round(deltaSec / 60);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  if (deltaSec < 60 * 60 * 24) {
    const h = Math.round(deltaSec / (60 * 60));
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  const d = Math.round(deltaSec / (60 * 60 * 24));
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

/** Render a single recent-change entry as plain English. */
function renderRecentChangeSummary(entry: AuditEntry): string {
  const actor = entry.actorDisplayName ?? 'Someone';
  const payload = entry.payload ?? {};

  if (entry.action === 'platform_feature.set_visibility') {
    const key = typeof payload.key === 'string' ? payload.key : '';
    const to = payload.to;
    const flag = FEATURE_FLAGS.find((f) => f.key === key);
    const label = flag?.label ?? key;
    const toLabel =
      typeof to === 'string' && (to === 'off' || to === 'founding_cohort' || to === 'all')
        ? visibilityLabel(to)
        : String(to ?? '');
    return `${actor} turned ${label} to ${toLabel}`;
  }
  if (entry.action === 'platform_phase.set') {
    const phase = payload.phase;
    const phaseLabel = isLaunchPhase(phase)
      ? LAUNCH_PHASE_LABELS[phase].label
      : String(phase ?? '');
    return `${actor} switched the platform to ${phaseLabel}`;
  }
  if (entry.action === 'platform_phase.applied_preset') {
    const phase = payload.phase;
    const phaseLabel = isLaunchPhase(phase)
      ? LAUNCH_PHASE_LABELS[phase].label
      : String(phase ?? '');
    const diff = Array.isArray(payload.diff) ? payload.diff : [];
    const n = diff.length;
    return `${actor} applied ${phaseLabel} defaults (${n} flag${n === 1 ? '' : 's'} changed)`;
  }
  return `${actor} · ${entry.action}`;
}

// ─── Route ────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/admin/launch')({
  component: AdminLaunch,
});

function AdminLaunch() {
  const qc = useQueryClient();

  const launchQuery = useQuery({
    queryKey: ['admin', 'launch'],
    queryFn: () => adminGet<LaunchStateResponse>('/api/admin/launch'),
  });

  const cohortQuery = useQuery({
    queryKey: ['admin', 'cohort'],
    queryFn: () => adminGet<Cohort>('/api/admin/cohort'),
  });

  // Local mirror so the phase Select can pre-show pending state. Falls back to
  // server data whenever a query refetches.
  const [pendingPhase, setPendingPhase] = useState<LaunchPhase | null>(null);
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const [presetTargetPhase, setPresetTargetPhase] = useState<LaunchPhase | null>(null);

  const setPhaseMutation = useMutation({
    mutationFn: (phase: LaunchPhase) =>
      adminPatch<{ ok: true }>('/api/admin/launch/phase', { phase }),
    onSuccess: () => {
      setPendingPhase(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'launch'] });
    },
    onError: () => {
      setPendingPhase(null);
    },
  });

  const setFeatureMutation = useMutation({
    mutationFn: ({ key, visibility }: { key: FeatureFlagKey; visibility: FeatureVisibility }) =>
      adminPatch<{ ok: true }>(`/api/admin/launch/features/${key}`, { visibility }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'launch'] });
    },
  });

  const applyPresetMutation = useMutation({
    mutationFn: (phase: LaunchPhase) =>
      adminPost<ApplyPresetResponse>('/api/admin/launch/apply-recommended', { phase }),
    onSuccess: () => {
      setPresetModalOpen(false);
      setPresetTargetPhase(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'launch'] });
    },
  });

  // Compute diff that "Apply Phase N defaults" would produce — same logic the
  // server uses, mirrored client-side so the confirm modal can show it.
  // Hook MUST live above the loading/error early returns: React #310 fires
  // when hook count changes between renders (loading → loaded).
  //
  // `flag.key` widens to `string` from `FeatureFlagDef` (the registry typing
  // doesn't narrow per-entry), but every entry in FEATURE_FLAGS is, by
  // construction, a member of FeatureFlagKey — so the cast is sound.
  const featuresForDiff = launchQuery.data?.features;
  const presetDiff = useMemo(() => {
    if (!presetTargetPhase || !featuresForDiff) return [];
    return FEATURE_FLAGS.filter((flag) => {
      const cur = featuresForDiff[flag.key as FeatureFlagKey];
      const rec = flag.recommended[presetTargetPhase];
      return cur !== rec;
    }).map((flag) => ({
      key: flag.key,
      label: flag.label,
      from: featuresForDiff[flag.key as FeatureFlagKey],
      to: flag.recommended[presetTargetPhase],
    }));
  }, [presetTargetPhase, featuresForDiff]);

  // Initial render — show skeletons rather than a blank screen.
  if (launchQuery.isLoading) {
    return (
      <AdminShell>
        <Stack gap="lg">
          <Title order={2}>Launch controls</Title>
          <Text c="dimmed" size="sm" maw={720}>
            Set the platform's current phase, then turn features on or off as you're ready
            for them. Changes propagate within a minute.
          </Text>
          <Card withBorder padding="md">
            <Stack gap="sm">
              <Skeleton height={20} width={180} radius="sm" />
              <Skeleton height={36} radius="sm" />
              <Skeleton height={14} width={320} radius="sm" />
            </Stack>
          </Card>
          <Card withBorder padding="md">
            <Stack gap="sm">
              <Skeleton height={20} width={220} radius="sm" />
              <Skeleton height={36} radius="sm" />
            </Stack>
          </Card>
          <Card withBorder padding="md">
            <Stack gap="sm">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} height={48} radius="sm" />
              ))}
            </Stack>
          </Card>
        </Stack>
      </AdminShell>
    );
  }

  if (launchQuery.isError || !launchQuery.data) {
    return (
      <AdminShell>
        <Stack gap="lg">
          <Title order={2}>Launch controls</Title>
          <Alert color="red" variant="light" title="Couldn't load launch state">
            <Stack gap="xs">
              <Text size="sm">
                {launchQuery.error instanceof Error
                  ? launchQuery.error.message
                  : 'The /api/admin/launch endpoint did not respond. Refresh and try again.'}
              </Text>
              <Group>
                <Button size="xs" variant="default" onClick={() => void launchQuery.refetch()}>
                  Retry
                </Button>
              </Group>
            </Stack>
          </Alert>
        </Stack>
      </AdminShell>
    );
  }

  const { phase: serverPhase, features, recentChanges } = launchQuery.data;
  const currentPhase: LaunchPhase = pendingPhase ?? serverPhase;

  return (
    <AdminShell>
      <Stack gap="lg">
        <div>
          <Title order={2}>Launch controls</Title>
          <Text c="dimmed" size="sm" maw={720} mt={6}>
            Set the platform's current phase, then turn features on or off as you're ready
            for them. Changes propagate within a minute.
          </Text>
        </div>

        {/* ── Phase card ───────────────────────────────────────────────── */}
        <Card withBorder padding="lg">
          <Stack gap="md">
            <Title order={4}>Current phase</Title>
            <Group align="flex-end" gap="md" wrap="wrap">
              <Select
                label="Launch phase"
                w={320}
                value={currentPhase}
                data={phaseSelectOptions()}
                allowDeselect={false}
                onChange={(v) => {
                  if (v === null || !isLaunchPhase(v)) return;
                  setPendingPhase(v);
                  setPhaseMutation.mutate(v);
                }}
                disabled={setPhaseMutation.isPending}
              />
              <Button
                variant="default"
                onClick={() => {
                  setPresetTargetPhase(currentPhase);
                  setPresetModalOpen(true);
                }}
              >
                Apply {LAUNCH_PHASE_LABELS[currentPhase].label.split(':')[0]} defaults
              </Button>
            </Group>
            <Text c="dimmed" size="sm">
              {LAUNCH_PHASE_LABELS[currentPhase].tagline}
            </Text>
            {setPhaseMutation.isError ? (
              <Text c="red" size="sm">
                Could not change phase. Please retry.
              </Text>
            ) : null}
          </Stack>
        </Card>

        {/* ── Cohort mini-card ─────────────────────────────────────────── */}
        <Card withBorder padding="md">
          <Stack gap="xs">
            <Title order={5}>Founding cohort</Title>
            {cohortQuery.isLoading ? (
              <Group gap="xs">
                <Loader size="xs" />
                <Text size="sm" c="dimmed">
                  Loading cohort data…
                </Text>
              </Group>
            ) : cohortQuery.data ? (
              <Stack gap={6}>
                <Text fw={600}>
                  {cohortQuery.data.claimed} / {cohortQuery.data.cap} founding venues claimed
                </Text>
                <Progress
                  value={Math.min(
                    100,
                    Math.round((cohortQuery.data.claimed / Math.max(1, cohortQuery.data.cap)) * 100),
                  )}
                  aria-label="Founding cohort progress"
                />
                <Text size="sm" c="dimmed">
                  When this fills, Phase 1 typically transitions to Phase 2.
                </Text>
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                Cohort data unavailable.
              </Text>
            )}
          </Stack>
        </Card>

        {/* ── Flags grid ───────────────────────────────────────────────── */}
        <Stack gap="lg">
          {CATEGORY_ORDER.map((category) => {
            const flagsInCategory = FEATURE_FLAGS.filter((f) => f.category === category);
            if (flagsInCategory.length === 0) return null;
            return (
              <Card key={category} withBorder padding="lg">
                <Stack gap="md">
                  <Title order={4}>{FEATURE_CATEGORY_LABELS[category]}</Title>
                  <Stack gap="lg">
                    {flagsInCategory.map((flag) => {
                      // Registry typing widens `flag.key` to `string`; every
                      // entry is FeatureFlagKey by construction.
                      const key = flag.key as FeatureFlagKey;
                      const currentVisibility = features[key];
                      const recommended = flag.recommended[currentPhase];
                      const overridden = currentVisibility !== recommended;
                      const isPending =
                        setFeatureMutation.isPending && setFeatureMutation.variables?.key === key;
                      return (
                        <Stack key={flag.key} gap={6}>
                          <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
                            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                              <Group gap="xs" wrap="wrap">
                                <Text fw={600}>{flag.label}</Text>
                                {flag.status === 'stubbed' ? (
                                  <Badge size="xs" variant="default" color="gray">
                                    Stubbed
                                  </Badge>
                                ) : null}
                                {overridden ? (
                                  <Badge size="xs" variant="light" color="yellow">
                                    Overridden
                                  </Badge>
                                ) : (
                                  <Badge size="xs" variant="light" color="tide">
                                    Matches recommended
                                  </Badge>
                                )}
                              </Group>
                              <Text c="dimmed" size="sm">
                                {flag.description}
                              </Text>
                            </Stack>
                            <SegmentedControl
                              value={currentVisibility}
                              onChange={(v) => {
                                if (v !== 'off' && v !== 'founding_cohort' && v !== 'all') return;
                                if (v === currentVisibility) return;
                                setFeatureMutation.mutate({ key, visibility: v });
                              }}
                              data={VISIBILITY_OPTIONS}
                              disabled={isPending}
                              size="xs"
                            />
                          </Group>
                          <Spoiler maxHeight={0} showLabel="Policy note" hideLabel="Hide">
                            <Text size="xs" c="dimmed" mt={6}>
                              {flag.policyNote} Recommended for{' '}
                              {LAUNCH_PHASE_LABELS[currentPhase].label.split(':')[0]}:{' '}
                              <strong>{visibilityLabel(recommended)}</strong>.
                            </Text>
                          </Spoiler>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Stack>

        {/* ── Recent changes ───────────────────────────────────────────── */}
        <Card withBorder padding="md">
          <Stack gap="sm">
            <Title order={5}>Recent changes</Title>
            {recentChanges.length === 0 ? (
              <Text size="sm" c="dimmed">
                No changes recorded yet.
              </Text>
            ) : (
              <Stack gap="xs">
                {recentChanges.slice(0, 20).map((entry) => (
                  <Group key={entry.id} justify="space-between" wrap="wrap" gap="xs">
                    <Text size="sm" style={{ flex: 1, minWidth: 0 }}>
                      {renderRecentChangeSummary(entry)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatRelativeTime(entry.createdAt)}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}
          </Stack>
        </Card>
      </Stack>

      {/* ── Apply-preset confirm modal ─────────────────────────────────── */}
      <Modal
        opened={presetModalOpen}
        onClose={() => {
          setPresetModalOpen(false);
          setPresetTargetPhase(null);
        }}
        title={
          presetTargetPhase
            ? `Apply ${LAUNCH_PHASE_LABELS[presetTargetPhase].label} defaults?`
            : 'Apply defaults?'
        }
        size="lg"
      >
        <Stack gap="md">
          {presetDiff.length === 0 ? (
            <Text size="sm">
              Every flag is already at its recommended value for this phase. Applying defaults
              will not change anything.
            </Text>
          ) : (
            <>
              <Text size="sm">
                This will change {presetDiff.length} flag{presetDiff.length === 1 ? '' : 's'}:
              </Text>
              <Stack gap={6}>
                {presetDiff.map((d) => (
                  <Text key={d.key} size="sm">
                    <strong>{d.label}</strong>: {visibilityLabel(d.from)} → {visibilityLabel(d.to)}
                  </Text>
                ))}
              </Stack>
            </>
          )}
          {applyPresetMutation.isError ? (
            <Text c="red" size="sm">
              Could not apply defaults. Please retry.
            </Text>
          ) : null}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => {
                setPresetModalOpen(false);
                setPresetTargetPhase(null);
              }}
              disabled={applyPresetMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!presetTargetPhase) return;
                applyPresetMutation.mutate(presetTargetPhase);
              }}
              loading={applyPresetMutation.isPending}
              disabled={presetDiff.length === 0}
            >
              Apply defaults
            </Button>
          </Group>
        </Stack>
      </Modal>
    </AdminShell>
  );
}
